import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import DataFrameFilterCreator from 'data-frames/filters/components/DataFrameFilterCreator';
import {DataFrameFilterOptions} from 'data-frames/filters/DataFrameFilterOptions';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {DataFrameReadOnlyInfo} from 'data-frames/header-menu/DataFrameReadOnlyInfo';
import {DataFrameRenameCreator} from 'data-frames/header-menu/DataFrameRenameCreator';
import {DataFrameRetypeCreator} from 'data-frames/header-menu/DataFrameRetypeCreator';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {readableTypeDef} from 'data-types/benchling-type-def/readableTypeDef';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option, {OptionProps} from 'ui-library/menus/Option';
import InteractiveHeaderCell, {IconInfo, IconVisibility} from 'ui-library/simple-table/InteractiveHeaderCell';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import {SimpleTableSortDirection} from 'ui-library/simple-table/useSimpleTableSortState';
import {capitalize} from 'util/NameUtil';

const useStyles = createStyles({
  filterContainer: {
    padding: 10,
    minWidth: 300,
    maxWidth: 500,
  },
  menuContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  typeIcon: {color: Color.GRAY_400},
});

const PLACEHOLDER_ICON_INFO: IconInfo = {icon: 'error', visibility: IconVisibility.HIDDEN, onClick: null};

const initialSortState = makeUseGlobalClientState<SimpleTableSortDirection | null>(null);

export enum PopdownState {
  CLOSED = 'CLOSED',
  MENU = 'MENU',
  FILTER = 'FILTER',
  RENAME = 'RENAME',
  RETYPE = 'RETYPE',
}

function getIconInfos(
  columnSystemName: string,
  popdownState: PopdownState,
  setPopdownState: (popdownState: PopdownState) => void,
  sortDirection: SimpleTableSortDirection | null,
  onSortIconClick: () => void,
  readOnlyInfo: DataFrameReadOnlyInfo,
  filterOptions: Pick<DataFrameFilterOptions, 'getFilterForColumnHeaders'>
): Array<IconInfo> {
  const icons: Array<IconInfo> = [];
  const filter = filterOptions.getFilterForColumnHeaders(columnSystemName);

  if (sortDirection) {
    icons.push({icon: sortDirection, visibility: IconVisibility.ALWAYS, onClick: onSortIconClick});
  }
  if (filter || popdownState === PopdownState.FILTER) {
    icons.push({
      icon: 'bars-filter',
      visibility: IconVisibility.ALWAYS,
      isDisabled: readOnlyInfo.allUpdatesDisabledMessage != null || readOnlyInfo.isReadOnly,
      tooltipText: readOnlyInfo.allUpdatesDisabledMessage,
      onClick: () => {
        setPopdownState(popdownState === PopdownState.FILTER ? PopdownState.CLOSED : PopdownState.FILTER);
      },
      color: Color.BLUE_400,
    });
  }
  if (!(sortDirection || filter)) {
    icons.push({
      icon: 'overflow',
      visibility: popdownState === PopdownState.MENU ? IconVisibility.ALWAYS : IconVisibility.ON_HOVER,
      onClick: () => {
        setPopdownState(PopdownState.MENU);
      },
      color: Color.GRAY_700,
    });
  }

  if (icons.length > 1) {
    return icons;
  } else {
    // Include a placeholder second icon, so columns don't widen/narrow as icons change.
    return [PLACEHOLDER_ICON_INFO, ...icons];
  }
}

type MenuOptions = Array<
  Pick<OptionProps<null>, 'icon' | 'onSelect' | 'isSelected' | 'tooltipTitle' | 'isDisabled'> & {
    title: string;
  }
>;

function getMenuOptions(
  columnSystemName: string,
  setSortDirection: (sortDirection: SimpleTableSortDirection | null) => void,
  setPopdownState: (popdownState: PopdownState) => void,
  sortDirection: SimpleTableSortDirection | null,
  headerMenuOptions: DataFrameHeaderMenuOptions,
  column: DataFrameColumnDef
): MenuOptions {
  const {filterOptions, hideColumnOptions} = headerMenuOptions;
  const options: MenuOptions = [
    {
      title: 'Sort ascending',
      icon: 'arrow-up',
      onSelect: () => {
        setSortDirection(sortDirection !== 'arrow-up' ? 'arrow-up' : null);
      },
      isSelected: sortDirection === 'arrow-up',
    },
    {
      title: 'Sort descending',
      icon: 'arrow-down',
      onSelect: () => {
        setSortDirection(sortDirection !== 'arrow-down' ? 'arrow-down' : null);
      },
      isSelected: sortDirection === 'arrow-down',
    },
  ];

  if (!headerMenuOptions.readOnlyInfo.isReadOnly) {
    options.push({
      title: 'Filter',
      icon: 'bars-filter',
      onSelect: () => {
        // Wrap with timer, because otherwise the click inside the menu will close the popdown.
        setTimeout(() => {
          setPopdownState(PopdownState.FILTER);
        }, 0);
      },
      isSelected:
        !headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage &&
        filterOptions.getFilterForColumnHeaders(columnSystemName) != null,
      tooltipTitle: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage,
      isDisabled: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage != null,
    });

    options.push({
      title: 'Hide column',
      icon: 'eye-slash',
      onSelect: () => {
        hideColumnOptions.hideColumn(columnSystemName);
      },
      isDisabled:
        headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage != null ||
        hideColumnOptions.disabledMessage != null,
      tooltipTitle:
        headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage ?? hideColumnOptions.disabledMessage,
    });

    options.push({
      title: 'Rename column',
      icon: 'pen',
      onSelect: () => {
        // Wrap with timer, because otherwise the click inside the menu will close the popdown.
        setTimeout(() => {
          setPopdownState(PopdownState.RENAME);
        }, 0);
      },
      isDisabled: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage != null,
      tooltipTitle: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage,
    });

    options.push({
      title: 'Convert column format',
      icon: columnToTypeIcon(column),
      onSelect: () => {
        // Wrap with timer, because otherwise the click inside the menu will close the popdown.
        setTimeout(() => {
          setPopdownState(PopdownState.RETYPE);
        }, 0);
      },
      isDisabled: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage != null,
      tooltipTitle: headerMenuOptions.readOnlyInfo.allUpdatesDisabledMessage,
    });
  }

  return options;
}

function DataFrameTableHeaderCellContents(
  props: Pick<
    DataFrameTableHeaderCellProps,
    'column' | 'columnNameToHeaderCellRenderFn' | 'columnNameToHeaderDecorationRenderFn'
  >
): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className="u-vertical-center-items">
      {props.columnNameToHeaderCellRenderFn?.hasOwnProperty(props.column.systemName) ? (
        props?.columnNameToHeaderCellRenderFn[props.column.systemName](props.column)
      ) : (
        <>
          <Tooltip
            placement={TooltipPlacement.TOP}
            title={capitalize(readableTypeDef(props.column.type.leafType))}
          >
            <BenchlingIcon className={classes.typeIcon} icon={columnToTypeIcon(props.column)} />
          </Tooltip>
          <span className="u-mini-spacing-left">{props.column.displayName}</span>
        </>
      )}
      {props.columnNameToHeaderDecorationRenderFn?.hasOwnProperty(props.column.systemName) &&
        props.columnNameToHeaderDecorationRenderFn[props.column.systemName](props.column)}
    </div>
  );
}

export interface DataFrameTableHeaderCellProps {
  clientStateId: string;
  column: DataFrameColumnDef;
  onToggleSort: (sortDirection?: SimpleTableSortDirection | null) => void;
  sortDirection: SimpleTableSortDirection | null;
  headerMenuOptions?: DataFrameHeaderMenuOptions;
  columnNameToHeaderCellRenderFn?: Record<string, (column: DataFrameColumnDef) => JSX.Element>;
  columnNameToHeaderDecorationRenderFn?: Record<string, (column: DataFrameColumnDef) => JSX.Element>;
}

export default function DataFrameTableHeaderCell(props: DataFrameTableHeaderCellProps): JSX.Element {
  const {classes} = useStyles();
  const [popdownState, setPopdownState] = useState<PopdownState>(PopdownState.CLOSED);
  const [initialSort, setInitialSort] = initialSortState.useGlobalClientState(props.clientStateId);

  if (props.headerMenuOptions == null) {
    return (
      <SortableHeaderCell sortIcon={props.sortDirection} onToggleSort={props.onToggleSort}>
        <DataFrameTableHeaderCellContents {...props} />
      </SortableHeaderCell>
    );
  }

  function setSortDirectionFromDropdown(newSortDirection: SimpleTableSortDirection | null): void {
    setInitialSort(newSortDirection);
    props.onToggleSort(newSortDirection);
  }

  function setSortDirectionFromIcon(): void {
    if (props.sortDirection === initialSort) {
      props.onToggleSort(props.sortDirection === 'arrow-down' ? 'arrow-up' : 'arrow-down');
    } else {
      props.onToggleSort(null);
    }
  }

  const iconInfos = getIconInfos(
    props.column.systemName,
    popdownState,
    setPopdownState,
    props.sortDirection,
    setSortDirectionFromIcon,
    props.headerMenuOptions.readOnlyInfo,
    props.headerMenuOptions.filterOptions
  );

  const menuOptions = getMenuOptions(
    props.column.systemName,
    setSortDirectionFromDropdown,
    setPopdownState,
    props.sortDirection,
    props.headerMenuOptions,
    props.column
  );

  const renderMenuPopdownContents = (): JSX.Element => (
    <div className={classes.menuContainer}>
      <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT}>
        {menuOptions.map(({title, ...optionProps}) => (
          <Option key={title} {...optionProps}>
            {title}
          </Option>
        ))}
      </Menu>
    </div>
  );

  const renderFilterPopdownContents = (): JSX.Element | null =>
    props.headerMenuOptions?.filterOptions == null ? null : (
      <div
        className={classes.filterContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DataFrameFilterCreator
          filterOptionsForCreator={{
            getFilterForColumnHeaders: props.headerMenuOptions.filterOptions.getFilterForColumnHeaders,
            updateFilterForColumn: props.headerMenuOptions.filterOptions.updateFilterForColumn,
          }}
          columns={[props.column]}
          initialSelectedColumnName={props.column.systemName}
          onCancel={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
          onSave={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
        />
      </div>
    );

  const renderRenameColumnPopdownContents = (): JSX.Element | null =>
    props.headerMenuOptions?.renameColumnOptions == null ? null : (
      <div
        className={classes.filterContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DataFrameRenameCreator
          renameColumnOptions={props.headerMenuOptions.renameColumnOptions}
          columns={[props.column]}
          initialSelectedColumnName={props.column.systemName}
          onCancel={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
          onSave={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
        />
      </div>
    );

  const renderRetypeColumnPopdownContents = (): JSX.Element | null =>
    props.headerMenuOptions?.retypeColumnOptions == null ? null : (
      <div
        className={classes.filterContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DataFrameRetypeCreator
          retypeColumnOptions={props.headerMenuOptions.retypeColumnOptions}
          columns={[props.column]}
          initialSelectedColumnName={props.column.systemName}
          onCancel={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
          onSave={() => {
            setPopdownState(PopdownState.CLOSED);
          }}
        />
      </div>
    );

  return (
    <InteractiveHeaderCell
      popdownInfo={{
        isOpen: popdownState !== PopdownState.CLOSED,
        renderContents: () => {
          switch (popdownState) {
            case PopdownState.MENU:
              return renderMenuPopdownContents();
            case PopdownState.FILTER:
              return renderFilterPopdownContents();
            case PopdownState.RENAME:
              return renderRenameColumnPopdownContents();
            case PopdownState.RETYPE:
              return renderRetypeColumnPopdownContents();
            default:
              return null;
          }
        },
        onRequestClose: () => {
          setPopdownState(PopdownState.CLOSED);
        },
      }}
      iconInfos={iconInfos}
      onClick={() => {
        setPopdownState(popdownState === PopdownState.CLOSED ? PopdownState.MENU : PopdownState.CLOSED);
      }}
    >
      <DataFrameTableHeaderCellContents {...props} />
    </InteractiveHeaderCell>
  );
}
